import { Dispatch, SetStateAction, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, MoreVertical, Plus } from "lucide-react";

import { FlowTag } from "@/components/Tags/FlowTag";
import { SpotToast } from "@/components/SpotToast";
import { FlowTagModal } from "./FlowTagModal";
import { TagActionsMenu } from "./TagActionsMenu";
import { useTagMutations } from "./useTagMutations";
import { TagsService, TagResponse } from "@/generated";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { Button } from "@/components/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/shadcn/dropdown-menu";
import { Checkbox } from "@/components/shadcn/checkbox";
import { DeleteDialog } from "../Modals/DeleteDialog";

/* Menu on the chip */
export const FlowTagsMenu = ({
  tags,
  selectedTags,
  setSelectedTags,
}: {
  tags: TagResponse[];
  selectedTags?: string[];
  setSelectedTags: Dispatch<SetStateAction<string[]>>;
}) => {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();

  const [tagToChange, setTagToChange] = useState<TagResponse | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  const { editTag, deleteTag } = useTagMutations({
    onSettled: () => {
      setTagToChange(undefined);
      setDeleteModalOpen(false);
    },
  });

  const createTagMutation = useMutation({
    mutationFn: TagsService.createTag,
    onSuccess: () => {
      // Invalidate all tag-related queries
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.TAGS],
      });

      // Invalidate all entity listings that might have tags (for consistency)
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.ASSISTANTS],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2],
      });

      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.TABLES],
      });

      SpotToast.success("Tag created successfully!");
    },
    onError: (error: any) => {
      if (error?.response?.status === 409) {
        SpotToast.error("Tag already exists");
      } else {
        SpotToast.error("Failed to create tag. Please try again later.");
      }
    },
  });

  const handleTagChange = (tag: { name: string; color: string }) => {
    if (selectedTags?.includes(tagToChange?.id || "")) {
      setSelectedTags(selectedTags.filter((t) => t !== tagToChange?.id));
    }

    editTag.mutate({
      path: {
        group_id: spotGroup.id,
        tag_id: tagToChange?.id || "",
      },
      body: {
        name: tag.name.trim(),
        color: tag.color,
      },
    });
  };

  const handleTagCreate = (tag: { name: string; color: string }) => {
    createTagMutation.mutate({
      path: { group_id: spotGroup.id },
      body: {
        name: tag.name,
        color: tag.color,
      },
    });
  };

  const handleTagDelete = () => {
    if (selectedTags?.includes(tagToChange?.id || "")) {
      setSelectedTags(selectedTags.filter((t) => t !== tagToChange?.id));
    }

    deleteTag.mutate({
      path: {
        group_id: spotGroup.id,
        tag_id: tagToChange?.id || "",
      },
    });
  };

  const handleSelectedTagsUpdate = (tags: string[]) => {
    setSelectedTags(tags);
  };

  const handleTagCheckChange = (tagValue: string, checked: boolean) => {
    if (checked) {
      setSelectedTags([...(selectedTags || []), tagValue]);
    } else {
      setSelectedTags((selectedTags || []).filter((t) => t !== tagValue));
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            data-testid="tags-filter"
            className={` font-normal shadow-xs hover:bg-background`}
            style={{
              color:
                selectedTags?.length === 0
                  ? "var(--color-muted-foreground)"
                  : "var(--color-foreground)",
            }}
          >
            {selectedTags?.length
              ? `${selectedTags.length} tag${selectedTags.length > 1 ? "s" : ""} filtered`
              : "Tags"}
            <ChevronDown
              className="h-4 w-4"
              color={
                selectedTags?.length === 0
                  ? "var(--color-muted-foreground)"
                  : "var(--color-foreground)"
              }
            />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="flex w-54 max-h-[min(20rem,var(--radix-dropdown-menu-content-available-height))] flex-col overflow-hidden p-1"
          align="end"
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            {tags.length === 0 ? (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                No tags available
              </div>
            ) : (
              tags.map((tag, index) => (
                <div
                  key={`${tag.id}-${index}`}
                  className="relative group flex items-center justify-between"
                >
                  <DropdownMenuItem
                    className="rounded-lg focus:bg-accent hover:bg-accent flex-1 min-w-0 flex items-center justify-between py-2 pr-2 cursor-pointer"
                    onSelect={(e) => {
                      e.preventDefault();
                      handleTagCheckChange(
                        tag.id,
                        !selectedTags?.includes(tag.id)
                      );
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Checkbox
                        checked={selectedTags?.includes(tag.id)}
                        className="pointer-events-none shrink-0"
                      />
                      <FlowTag tag={tag} />
                    </div>

                    <TagActionsMenu
                      modal
                      onEdit={() => {
                        setTagToChange(tag);
                        setTagModalOpen(true);
                      }}
                      onDelete={() => {
                        document.body.click();
                        setTimeout(() => {
                          setTagToChange(tag);
                          setDeleteModalOpen(true);
                        }, 50);
                      }}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-all duration-200 hover:bg-accent hover:opacity-100 shrink-0 absolute right-2"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <MoreVertical className="h-3 w-3" />
                        <span className="sr-only">Tag actions</span>
                      </Button>
                    </TagActionsMenu>
                  </DropdownMenuItem>
                </div>
              ))
            )}
          </div>
          <DropdownMenuSeparator className="shrink-0" />
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setTagToChange(undefined);
              setTagModalOpen(true);
            }}
            className="cursor-pointer shrink-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create new tag
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {tagModalOpen && (
        <FlowTagModal
          isOpen
          onOpenChange={setTagModalOpen}
          onConfirm={!!tagToChange ? handleTagChange : handleTagCreate}
          tag={tagToChange}
          isEdit={!!tagToChange}
        />
      )}

      <DeleteDialog
        title="Delete tag"
        text="Are you sure you want to delete this tag?"
        cancelLabel="Cancel"
        confirmLabel="Delete"
        isOpen={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onDelete={handleTagDelete}
      />
    </>
  );
};
